import { ApiSet, BaseResponse, IndexApiSet, useDeleteApi, useIndexApi, usePostApi, usePutApi } from 'utils/network/api_hooks'
import { Form, useEffectSkipFirst } from 'utils/hooks'
import { CraifHttpClient } from '../utils/network/axios'
import { PagingResponse } from 'entities'
import { StandardUrine, StandardUrineForm, StandardUrineSearchForm } from 'entities/standard_urine'

type StandardUrinesResponse = PagingResponse & {
    results: StandardUrine[]
}

export function useFetchStandardUrinesApi(searchForm?: Form<StandardUrineSearchForm>): IndexApiSet<StandardUrinesResponse> & { execute: () => void } {
    const apiPath = 'standard_urines/'
    const api = useIndexApi<StandardUrinesResponse>(new CraifHttpClient(), {
        initialResponse: { count: 0, results: [] },
        initialState: { page: searchForm?.object.page || 1, perPage: searchForm?.object.perPage || 50 },
    })

    const execute = (): void => {
        api.execute(apiPath, { params: searchForm?.object })
    }

    useEffectSkipFirst(() => {
        searchForm?.update((f) => {
            if (api.pageSet.page) f.page = api.pageSet.page
            if (api.pageSet.perPage) f.perPage = api.pageSet.perPage
        })
    }, [api.pageSet.page, api.pageSet.perPage])

    useEffectSkipFirst(() => {
        execute()
    }, [searchForm?.object])

    return { ...api, execute: execute }
}

export function usePostStandardUrineApi(): ApiSet<BaseResponse> & { execute: (form: Form<StandardUrineForm>) => void } {
    const apiSet = usePostApi<BaseResponse, StandardUrineForm>(
        new CraifHttpClient(),
        {
            initialResponse: {},
        },
        { formatJson: true }
    )

    const execute = (form: Form<StandardUrineForm>) => {
        const apiPath = `standard_urines/`
        apiSet.execute(apiPath, form)
    }

    return { ...apiSet, execute: execute }
}

export function usePutStandardUrineApi(): ApiSet<BaseResponse> & { execute: (form: Form<StandardUrineForm>) => void } {
    const apiSet = usePutApi<BaseResponse, StandardUrineForm>(
        new CraifHttpClient(),
        {
            initialResponse: {},
        },
        { formatJson: true }
    )

    const execute = (form: Form<StandardUrineForm>) => {
        const apiPath = `standard_urines/${form.object.id}/`
        apiSet.execute(apiPath, form.object)
    }

    return { ...apiSet, execute: execute }
}

export function useBulkPutStandardUrinesApi(): ApiSet<BaseResponse> & { execute: (form: Form<StandardUrineForm[]>) => void } {
    const apiSet = usePutApi<BaseResponse, StandardUrineForm[]>(
        new CraifHttpClient(),
        {
            initialResponse: {},
        },
        { formatJson: true }
    )

    const execute = (form: Form<StandardUrineForm[]>) => {
        const apiPath = `standard_urines/bulk_update/`
        apiSet.execute(apiPath, form.object)
    }

    return { ...apiSet, execute: execute }
}

export function useDeleteStandardUrineApi(): ApiSet<BaseResponse> & {
    execute: (id: number) => void
} {
    const api = useDeleteApi<BaseResponse>(new CraifHttpClient(), {
        initialResponse: {},
    })

    const execute = (id: number): void => {
        const apiPath = `standard_urines/${id}/`
        api.execute(apiPath)
    }

    return { ...api, execute: execute }
}
